import React, { useEffect, useState } from "react";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { Button, InlineLoader, Popover, SubTable, Text, useTable } from "@netapp/bxp-design-system-react";
import { apiClient } from "utils/clients";
import externals from "utils/externals";
import { ErrorIcon, NoticeTriangleIcon } from "@netapp/bxp-style/react-icons/Notification";
import styles from "./OrgAdminDetails.module.scss";
import ClaimOrgAdminDialog from "./ClaimOrgAdminDialog";
import { claimOrgAdmin as i18nNamespace } from "../../../../i18n/namespace";
import { useTranslation } from "react-i18next";
const OrgAdminListTable = React.memo(({orgAdmins}) => {
    const tableColumns = [{
        accessor: 'name',
        id: 'name',
        width: 250
    }];
    const {organizedRows, columns} = useTable({columns: tableColumns, rows: _.drop(orgAdmins)});
    return <>
        <SubTable headerContainerClassName={styles['header-row-hidden']} columns={columns} rows={organizedRows}/>
    </>
})

const OrgAdminDetails = React.memo(() => {
    const dispatch = useDispatch();
    const { selectedOrgId } = useSelector(state => state.tenancy);
    const featuresByKey = useSelector(state => state.features.byKey);
    const [isFetchOrgAdminsInprogress, setIsFetchOrgAdminsInprogress] = useState(true);
    const [hasOrgAdmin, setHasOrgAdmin] = useState(false);
    const [hasMultipleOrgAdmins, setHasMultipleOrgAdmins] = useState(false);
    const [orgAdminsList, setOrgAdminsList] = useState([]);
    const [hasAPIFailed, setHasAPIFailed] = useState(false);
    const [onContactAdminBtnClick, setOnContactAdminBtnClick] = useState(false);
    const [launchClaimDialog, setLaunchClaimDialog] = useState(false);
    const [isRefreshRequired, setIsRefreshRequired] = useState(true);
    const { t } = useTranslation(i18nNamespace);

    const onAPIFailure = (err) => {
        setIsFetchOrgAdminsInprogress(false);
        setHasAPIFailed(true);
        dispatch({
            type: 'NOTIFICATIONS:ERROR',
            payload: {
                message: 'Failed to fetch organization admin details.',
                exception: err
            }
        });
    }

    useEffect(() => {
        const openEmailClient = () => {
            try {
                const recipient = _.map(orgAdminsList, 'email').join(',');
                window.location.href = `mailto:${recipient}`;
            } catch (err) {
                dispatch({
                    type: 'NOTIFICATIONS:ERROR',
                    payload: {
                        message: 'Failed to launch email client.',
                        exception: err
                    }
                });
            } finally {
                setOnContactAdminBtnClick(false);
            }
        };
        if (onContactAdminBtnClick) openEmailClient();
    }, [onContactAdminBtnClick])

    useEffect(() => {
        const fetchOrgAdmins = async () => {
            try {
                setIsFetchOrgAdminsInprogress(true)
                const isSafeModeEnabled = featuresByKey?.["NetApp.TenancyV4/safemode"]?.active;
                const requestHeaders = {
                    headers: {
                        'Accept': isSafeModeEnabled
                            ? 'application/vnd.netapp.bxp.user.safemode+json, application/vnd.netapp.bxp.users.extended+json'
                            : 'application/vnd.netapp.bxp.users.extended+json'
                    }
                };
                const {data: {count, items}} = await apiClient.get(`${externals.tenancyV4Backend}/resources/${selectedOrgId}/permissions/bxp:organization-delete/users?filter=userType eq 'user'&limit=1000`, requestHeaders);
                setOrgAdminsList(items);
                setHasMultipleOrgAdmins(count > 1);
                setHasOrgAdmin(count > 0);
            } catch (err) {
                onAPIFailure(err);
            } finally {
                setIsRefreshRequired(false);
                setIsFetchOrgAdminsInprogress(false);
            }
        }
        if(isRefreshRequired) fetchOrgAdmins();
    }, [isRefreshRequired])

    return <>
        { isFetchOrgAdminsInprogress ? <InlineLoader /> : <>
                { hasAPIFailed && <div className={styles['org-admin-content-value']}>
                        <ErrorIcon color='error' size='16px' />
                        <Text style={{ paddingLeft: '8px' }}>{t('unable_to_retrieve')}</Text>
                    </div>}
                { !hasOrgAdmin && !hasAPIFailed && <div className={styles['org-admin-content-value']}>
                        <NoticeTriangleIcon color='error' size='16px' />
                        <Text style={{ paddingLeft: '8px' }}>{t('unclaimed')}</Text>
                    </div>}
                { hasOrgAdmin && !hasAPIFailed && <div>
                        <Text style={{display: 'inline-flex'}}>{orgAdminsList[0]?.name}</Text>
                        { hasMultipleOrgAdmins && <Popover placement='right' verticalPlacement='top' contentClassName={styles['popover-content-style']} Trigger={() => {
                            return <Button variant="text" style={{paddingLeft: '5px'}}>{`+${orgAdminsList?.length - 1}`}</Button>
                        }}>
                            <OrgAdminListTable orgAdmins={orgAdminsList}></OrgAdminListTable>
                        </Popover>}
                    </div>
                }
                <div className={styles['contact-admin-action-btn']}>
                    {hasOrgAdmin && !hasAPIFailed && <Button variant="text"
                            onClick={() => setOnContactAdminBtnClick(true)}>
                        {t('contact_admins')}
                    </Button>}
                    {!hasOrgAdmin && !hasAPIFailed && <Button variant="text"
                            onClick={() => setLaunchClaimDialog(true)}>
                        {t('claim_role')}
                    </Button>}
                </div>
            </>
        }
        {launchClaimDialog && <ClaimOrgAdminDialog closeTrigger={(isRefreshRequired) => {
                if (isRefreshRequired) setIsRefreshRequired(true);
                setLaunchClaimDialog(false);
            }
        } />}
    </>
});

export default OrgAdminDetails;